import { CountryCode, NationalNumber } from './countries';
import { getCountryCodeFromDialingCode } from './countryUtils/getCountryCode';
import {
  getDialingCodeFromCountryCode,
  getDialingCodeFromPhoneNumber,
} from './countryUtils/getDialingCode';
import { formatPartialPhoneNumber } from './formatPartialPhoneNumber';
import { isValidPhoneNumber } from './isValidPhoneNumber';

type PhoneNumberInfo = {
  /** phoneNumber formatted according to country of origin */
  phoneNumber: NationalNumber;
  countryCode: CountryCode;
  isValid: boolean;
};

const hasCountryCodeInPhoneNumber = (phoneNumber: string) =>
  phoneNumber.trim().startsWith('+');

export const parseIncompletePhoneNumber = (
  partialNumber: string,
  countryCode: CountryCode
): PhoneNumberInfo => {
  const normalizedNumber = partialNumber.trim();
  const selectedDialingCode = getDialingCodeFromCountryCode(countryCode);

  try {
    const actualDialingCode = getDialingCodeFromPhoneNumber(
      partialNumber,
      countryCode
    );
    const guessedCountryCodeFromTypedNumber =
      getCountryCodeFromDialingCode(actualDialingCode);
    const hasTypedDialingCode = hasCountryCodeInPhoneNumber(partialNumber);

    /**
     * We only try to correct the country dropdown selection if the user manually
     * typed the country as part of the phonenumber and its a completely different
     * dialing code than the one selected in the dropdown. This prevent issues
     * when trying to input a Canadian number would reset the dropdown to US.
     * (Different countries can have the same dialing code, like +1, which is
     * used for US, Puerto Rico and Canada).
     */
    const normalizedCountryCode =
      hasTypedDialingCode && actualDialingCode !== selectedDialingCode
        ? guessedCountryCodeFromTypedNumber
        : countryCode;

    return {
      countryCode: normalizedCountryCode,
      phoneNumber: formatPartialPhoneNumber(normalizedNumber, countryCode),
      isValid: isValidPhoneNumber(normalizedNumber, normalizedCountryCode),
    };
  } catch {
    // if phoneNumber is empty, consider it valid: user didnt type anything yet
    const isValid = isValidPhoneNumber(normalizedNumber, countryCode);

    return {
      countryCode,
      phoneNumber: formatPartialPhoneNumber(normalizedNumber, countryCode),
      isValid,
    };
  }
};
